import { usersService } from '@/api/users.service';
import { Alert } from '@/services/alert.service';
import { shouldValidateVatIdViaVies } from '@/utils/eu-countries';
import { normalizeVatId } from '@/utils/vat-id';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

export function useVatIdValidation() {
  const { t } = useI18n();
  const isValidatingVatId = ref(false);

  const validateVatId = async (
    vatId?: string | null,
    previousVatId?: string | null,
    country?: string | null,
  ): Promise<boolean> => {
    const normalizedVatId = normalizeVatId(vatId);
    const normalizedPreviousVatId = normalizeVatId(previousVatId);
    if (!normalizedVatId) return true;
    if (normalizedVatId === normalizedPreviousVatId) return true;
    if (!shouldValidateVatIdViaVies(country)) return true;

    isValidatingVatId.value = true;
    try {
      const result = await usersService.validateVatId(normalizedVatId, country);

      if (result.status === 'valid') return true;

      if (result.status === 'invalid') {
        await Alert.fire({
          icon: 'error',
          titleText: t('vatIdInvalidTitle'),
          text: t('vatIdInvalidText', { vatId: result.normalizedVatId }),
        });
        return false;
      }

      const { isConfirmed } = await Alert.fire({
        icon: 'warning',
        titleText: t('vatIdUnavailableTitle'),
        text: t('vatIdUnavailableText', { vatId: result.normalizedVatId }),
        showCancelButton: true,
        confirmButtonText: t('continueAnyway'),
        cancelButtonText: t('cancel'),
      });

      return isConfirmed;
    } finally {
      isValidatingVatId.value = false;
    }
  };

  return { isValidatingVatId, validateVatId };
}
